import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useMeetingAppContext } from "../MeetingAppContextDef";
import { ParticipantView } from "./ParticipantView";
import { useMeeting, useParticipant } from "../config/sdkConfig";
import { useSiteContext } from "../context/SiteContext";
import useIsMobile from "../hooks/useIsMobile";

// Detects if participant's video is portrait (mobile) or landscape (desktop)
// and renders the tile with the correct aspect ratio
function AdaptiveAspectTile({ participantId, children }) {
  const { webcamStream, webcamOn } = useParticipant(participantId);
  const [isPortrait, setIsPortrait] = useState(false);

  useEffect(() => {
    if (!webcamOn || !webcamStream) {
      setIsPortrait(false);
      return;
    }
    try {
      const track = webcamStream.track || webcamStream;
      const settings = track?.getSettings?.();
      if (settings?.width && settings?.height) {
        setIsPortrait(settings.height > settings.width);
      }
    } catch {}
  }, [webcamStream, webcamOn]);

  return (
    <div
      className="flex items-center justify-center overflow-hidden rounded-lg w-full"
      style={{ aspectRatio: isPortrait ? '9/16' : '16/9', flexShrink: 0 }}
    >
      {children}
    </div>
  );
}

// Component to manage pause/resume for participant streams based on visibility
// Adaptive quality: <=10 participants = all HIGH, >10 = only active speaker HIGH
function ParticipantStreamController({
  participantId,
  isVisible,
  isPresenting,
  participantIds,
}) {
  const { presenterId, pinnedParticipants, sustainedSpeakerId, meetingSettings } = useMeeting();
  const {
    webcamStream,
    stopConsumingWebcamStreams,
    consumeWebcamStreams,
    setQuality,
    isActiveSpeaker,
    metaData,
  } = useParticipant(participantId, {
    onVideoQualityChanged: (quality) => {
      console.log("quality changed", quality, "for participant", participantId);
    },
  });

  const isThisParticipantPresenter = presenterId && String(presenterId) === String(participantId);

  // Check if this participant is pinned (handles string/number ID mismatches)
  const isPinned = pinnedParticipants && (
    pinnedParticipants.has(participantId) ||
    pinnedParticipants.has(String(participantId)));

  // Use sustained speaker for quality decisions to prevent flickering
  const isSustainedSpeaker = sustainedSpeakerId &&
    String(participantId) === String(sustainedSpeakerId);

  // Webinar mode: the host/co-host/presenter/speaker is on stage as a
  // continuous broadcaster, not a meeting participant. Downgrading them to
  // the LOW (320×180) simulcast stream the moment they pause speaking made
  // the host's video look like ~360p mid-presentation. For webinars we
  // disable the active-speaker downgrade entirely for stage roles — the
  // viewer always sees them at full HIGH stream quality.
  const isWebinar = meetingSettings?.roomType === 'Webinar';
  const STAGE_ROLES = ['moderator', 'host', 'co-host', 'presenter', 'speaker'];
  const isStageRole = STAGE_ROLES.includes(metaData?.meetingRole);

  // Determine quality without isActiveSpeaker for small meetings (avoids 200ms re-triggers)
  const qualityRef = useRef(null);

  useEffect(() => {
    if (webcamStream) {
      try {
        if (!isVisible) {
          stopConsumingWebcamStreams();
        } else if (isVisible) {
          consumeWebcamStreams();

          // Adaptive quality logic (priority order):
          // 1. Webinar stage roles (host/co-host/presenter/speaker) always HIGH
          //    — they're on stage continuously, not "audience"
          // 2. Presenters always get HIGH quality
          // 3. Pinned participants always get HIGH quality
          // 4. Small meetings (<=10): everyone gets HIGH quality
          // 5. Large meetings (>10): sustained speaker gets HIGH, others LOW
          let targetQuality;
          if (isWebinar && isStageRole) {
            targetQuality = "high";
          } else if (isThisParticipantPresenter) {
            targetQuality = "high";
          } else if (isPinned) {
            targetQuality = "high";
          } else if (participantIds.length <= 10) {
            targetQuality = "high";
          } else {
            targetQuality = (isSustainedSpeaker || isActiveSpeaker) ? "high" : "low";
          }

          // Only call setQuality when it actually changes to avoid unnecessary Agora SDK calls
          if (targetQuality !== qualityRef.current) {
            qualityRef.current = targetQuality;
            setQuality(targetQuality);
          }
        }
      } catch (error) {
        console.error(`Error controlling stream for ${participantId}:`, error);
      }
    }
  }, [isVisible, webcamStream, participantId, isPresenting, isThisParticipantPresenter, participantIds.length, isActiveSpeaker, isPinned, isSustainedSpeaker, isWebinar, isStageRole]);

  return null;
}

// Hook to get participant activity data
function useParticipantActivity(participantId) {
  const { webcamOn, micOn, isActiveSpeaker } = useParticipant(participantId);
  return { webcamOn, micOn, isActiveSpeaker };
}

const MemoizedParticipant = React.memo(
  ParticipantView,
  (prevProps, nextProps) => {
    return (
      prevProps.participantId === nextProps.participantId &&
      prevProps.isSingleParticipant === nextProps.isSingleParticipant
    );
  }
);

function ParticipantGrid({
  participantIds,
  isPresenting,
  isMeetingContainer,
  isFlexCol,
  layout,
}) {
  const { sideBarMode, activeSpeakerId, setGridPagination, branding } = useMeetingAppContext();
  const { meetingSettings } = useSiteContext();
  const { participants, pinnedParticipants } = useMeeting();
  const isMobile = useIsMobile();
  const isRecorderMode = new URLSearchParams(window.location.search).get('recorder') === 'true';
  const isWebinarRecording = isRecorderMode && meetingSettings?.roomType === 'Webinar';

  // Track actual container width to make grid responsive to sidebar state
  const gridContainerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  useEffect(() => {
    const el = gridContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
        setContainerHeight(entry.contentRect.height);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  // console.log("isMobile", isMobile); // commented out to reduce console noise

  // Helper functions for string-based comparison (handles type mismatches between string and numeric IDs)
  const includesById = (arr, id) => arr.some(item => String(item) === String(id));
  const filterOutById = (arr, id) => arr.filter(item => String(item) !== String(id));

  // Pinned/spotlighted participants come first across all gallery render paths.
  // Spotlight is broadcast via Socket.IO and applied to pinnedParticipants on every client,
  // so reordering here makes the spotlighted person appear in position 1 for everyone.
  const isPinnedId = (id) =>
    !!pinnedParticipants && (
      pinnedParticipants.has(id) ||
      pinnedParticipants.has(String(id)) ||
      pinnedParticipants.has(Number(id))
    );
  const orderedParticipantIds = useMemo(() => {
    if (!pinnedParticipants || pinnedParticipants.size === 0) return participantIds;
    const pinned = participantIds.filter(isPinnedId);
    if (pinned.length === 0) return participantIds;
    const rest = participantIds.filter(id => !isPinnedId(id));
    return [...pinned, ...rest];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantIds, pinnedParticipants]);

  const [participantsPerPage, setParticipantsPerPage] = useState(4);
  const [currentPage, setCurrentPage] = useState(0);
  const [gridCurrentPage, setGridCurrentPage] = useState(0);
  const [gridParticipantsPerPage, setGridParticipantsPerPage] = useState(3);

  const [stableVisibleParticipants, setStableVisibleParticipants] = useState(
    []
  );

  // Get minimized state from context
  const { isParticipantRowMinimized, setIsParticipantRowMinimized } = useMeetingAppContext();

  // Drag state for pagination panel
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [panelPosition, setPanelPosition] = useState({ x: 0, y: 0 }); // Will be set to bottom-right in useEffect

  const totalParticipants = participantIds.length;

  // Clamp pages to valid range when participant count or page size changes
  // (instead of resetting to 0, which causes the page to bounce back)
  useEffect(() => {
    const gridPageSize = isMobile && !isPresenting ? 4 : gridParticipantsPerPage;
    const maxGridPages = Math.ceil(totalParticipants / gridPageSize);

    if (maxGridPages > 0 && gridCurrentPage >= maxGridPages) {
      setGridCurrentPage(maxGridPages - 1);
    }

    const maxPresenterPages = Math.max(0, totalParticipants - participantsPerPage);
    if (currentPage > maxPresenterPages) {
      setCurrentPage(Math.max(0, maxPresenterPages));
    }
  }, [
    totalParticipants,
    gridParticipantsPerPage,
    gridCurrentPage,
    currentPage,
    participantsPerPage,
    isMobile,
    isPresenting,
  ]);

  useEffect(() => {
    // Handle sidebar mode changes without resetting pages
    if (sideBarMode) {
      setParticipantsPerPage(3);
      setGridParticipantsPerPage(20);
    } else {
      if (isFlexCol) {
        // Speaker-side layout - vertical filmstrip
        setParticipantsPerPage(3);
        setGridParticipantsPerPage(20);
      } else {
        // Speaker-top layout - horizontal filmstrip
        setParticipantsPerPage(4);
        setGridParticipantsPerPage(20);
      }
    }
  }, [sideBarMode, isFlexCol]);

  // Publish pagination state to context so bottom bar can render controls
  useEffect(() => {
    if (!isPresenting && !isMobile) {
      const totalPages = gridParticipantsPerPage > 0 ? Math.ceil(totalParticipants / gridParticipantsPerPage) : 0;
      setGridPagination({
        currentPage: gridCurrentPage,
        totalPages,
        goToPage: setGridCurrentPage,
      });
    } else {
      setGridPagination({ currentPage: 0, totalPages: 0, goToPage: null });
    }
  }, [gridCurrentPage, totalParticipants, gridParticipantsPerPage, isPresenting, isMobile, setGridPagination]);

  // Set initial position to bottom-right and handle window resize
  useEffect(() => {
    const updatePosition = () => {
      setPanelPosition({
        x: window.innerWidth - 200, // 200px from right edge
        y: window.innerHeight - 80, // 80px from bottom edge
      });
    };

    // Set initial position
    updatePosition();

    // Handle window resize
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("resize", updatePosition);
    };
  }, []);

  // Drag handlers for pagination panel
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;

    // Constrain to viewport bounds
    const maxX = window.innerWidth - 200; // Approximate panel width
    const maxY = window.innerHeight - 60; // Approximate panel height

    setPanelPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add global mouse event listeners when dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "none"; // Prevent text selection during drag

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.userSelect = "";
      };
    }
  }, [isDragging, dragOffset]);

  // Component to collect participant activity data
  const ParticipantActivityCollector = ({
    participantId,
    onActivityChange,
  }) => {
    const { webcamOn, micOn, isActiveSpeaker } = useParticipant(participantId);
    const prevRef = useRef({ webcamOn, micOn, isActiveSpeaker });

    useEffect(() => {
      const prev = prevRef.current;
      if (
        prev.webcamOn !== webcamOn ||
        prev.micOn !== micOn ||
        prev.isActiveSpeaker !== isActiveSpeaker
      ) {
        prevRef.current = { webcamOn, micOn, isActiveSpeaker };
        onActivityChange(participantId, { webcamOn, micOn, isActiveSpeaker });
      }
    }, [webcamOn, micOn, isActiveSpeaker, participantId, onActivityChange]);

    return null;
  };

  // State to track all participant activities
  const [participantActivities, setParticipantActivities] = useState({});

  // Handler for activity updates (memoized) and only updates when changed
  const handleActivityChange = useCallback((participantId, activity) => {
    setParticipantActivities((prev) => {
      const current = prev[participantId];
      if (
        current?.webcamOn === activity.webcamOn &&
        current?.micOn === activity.micOn &&
        current?.isActiveSpeaker === activity.isActiveSpeaker
      ) {
        return prev;
      }
      return { ...prev, [participantId]: activity };
    });
  }, []);

  useEffect(() => {
    if (!isMobile || isPresenting) {
      return;
    }

    setStableVisibleParticipants((prev) => {
      if (prev.length === 0 && participantIds.length > 0) {
        return participantIds.slice(0, 4);
      }

      // Use string comparison to handle type mismatches (string vs numeric IDs)
      const validParticipants = prev.filter((id) =>
        includesById(participantIds, id)
      );

      if (validParticipants.length < prev.length) {
        const remainingSlots = 4 - validParticipants.length;
        const availableParticipants = participantIds.filter(
          (id) => !includesById(validParticipants, id)
        );
        const newParticipants = availableParticipants.slice(0, remainingSlots);
        return [...validParticipants, ...newParticipants];
      }

      if (participantIds.length < 4) {
        return participantIds.slice(0, 4);
      }

      return prev;
    });
  }, [participantIds, isMobile, isPresenting]);

  const prevActiveSpeakerRef = useRef(null);
  useEffect(() => {
    if (!isMobile || isPresenting || !activeSpeakerId) {
      prevActiveSpeakerRef.current = activeSpeakerId;
      return;
    }

    // For <=10 participants, don't swap tiles when active speaker changes.
    // Positions are stable (host-first) and should not shuffle.
    if (participantIds.length <= 10) {
      prevActiveSpeakerRef.current = activeSpeakerId;
      return;
    }

    if (activeSpeakerId === prevActiveSpeakerRef.current) {
      return;
    }

    prevActiveSpeakerRef.current = activeSpeakerId;

    setStableVisibleParticipants((prev) => {
      // Use string comparison to handle type mismatches (activeSpeakerId is string, UIDs might be numbers)
      if (includesById(prev, activeSpeakerId)) {
        return prev;
      }

      if (prev.length === 4) {
        const activities = prev.map((id) => participantActivities[id] || {});

        let replaceIndex = -1;
        for (let i = 0; i < activities.length; i++) {
          const activity = activities[i];
          if (!activity.webcamOn && !activity.micOn) {
            replaceIndex = i;
            break;
          }
        }

        if (replaceIndex === -1) {
          replaceIndex = 0;
        }

        const newStableList = [...prev];
        newStableList[replaceIndex] = activeSpeakerId;
        return newStableList;
      }

      if (!includesById(prev, activeSpeakerId)) {
        return [...prev, activeSpeakerId].slice(0, 4);
      }

      return prev;
    });
  }, [activeSpeakerId, isMobile, isPresenting, participantActivities, participantIds.length]);

  // Sort participants by activity (for both mobile and desktop views)
  // Priority: pinned/spotlighted (broadcast by host) > isActiveSpeaker > webcamOn > micOn
  // For <=10 participants: only pinning reorders — otherwise preserve host-first prop order
  // For >10 participants: full sort with pinning at the top
  const sortedParticipantIds = useMemo(() => {
    if (participantIds.length <= 10) {
      return orderedParticipantIds;
    }
    return [...participantIds].sort((a, b) => {
      const pinnedA = isPinnedId(a);
      const pinnedB = isPinnedId(b);
      if (pinnedA && !pinnedB) return -1;
      if (!pinnedA && pinnedB) return 1;

      const activityA = participantActivities[a] || {};
      const activityB = participantActivities[b] || {};

      if (activityA.isActiveSpeaker && !activityB.isActiveSpeaker) return -1;
      if (!activityA.isActiveSpeaker && activityB.isActiveSpeaker) return 1;

      if (activityA.webcamOn && !activityB.webcamOn) return -1;
      if (!activityA.webcamOn && activityB.webcamOn) return 1;

      if (activityA.micOn && !activityB.micOn) return -1;
      if (!activityA.micOn && activityB.micOn) return 1;

      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantIds, participantActivities, orderedParticipantIds, pinnedParticipants]);

  // Helper function to check if a participant is currently visible
  const isParticipantVisible = (index) => {
    if (isPresenting) {
      return index >= currentPage && index < currentPage + participantsPerPage;
    } else {
      const startIndex = gridCurrentPage * gridParticipantsPerPage;
      return (
        index >= startIndex && index < startIndex + gridParticipantsPerPage
      );
    }
  };

  // Mobile-specific layout for grid view
  if (isMobile && !isPresenting) {

    // ── SMALL MEETINGS (<=10): stable, scrollable list ──
    // Use participantIds directly (host-first order from props).
    // No pagination, no stableVisibleParticipants, no speaker-based reordering.
    if (participantIds.length <= 10) {
      // Use explicit viewport-based height so the container scrolls on mobile.
      // The parent flex chain doesn't constrain height, so we calculate it ourselves:
      // 100dvh - 64px (top bar) - 80px (bottom bar) - 16px (padding)
      return (
        <div
          className="w-full rounded-xl"
          style={{
            height: 'calc(100dvh - 64px - 80px - 16px)',
            maxHeight: 'calc(100dvh - 64px - 80px - 16px)',
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {/* Activity collectors (hidden) */}
          {participantIds.map((participantId) => (
            <ParticipantActivityCollector
              key={`activity_${participantId}`}
              participantId={participantId}
              onActivityChange={handleActivityChange}
            />
          ))}
          {/* Stream controllers — all visible since we show everyone */}
          {participantIds.map((participantId) => (
            <ParticipantStreamController
              key={`stream_controller_${participantId}`}
              participantId={participantId}
              isVisible={true}
              isPresenting={isPresenting}
              participantIds={participantIds}
            />
          ))}

          {/* Scrollable participant list — pinned/spotlighted first, then host-first */}
          <div className="flex flex-col gap-2 w-full p-2">
            {orderedParticipantIds.map((participantId) => (
              <AdaptiveAspectTile key={`mobile_${participantId}`} participantId={participantId}>
                <MemoizedParticipant
                  participantId={participantId}
                  isSingleParticipant={participantIds.length === 1}
                />
              </AdaptiveAspectTile>
            ))}
          </div>
        </div>
      );
    }

    // ── LARGE MEETINGS (>10): paginated with stableVisibleParticipants ──
    const MOBILE_PAGE_SIZE = 4;

    let firstPageParticipants = [];

    // Seed pinned/spotlighted participants FIRST so they're guaranteed visible,
    // even when stableVisibleParticipants already filled the page.
    if (pinnedParticipants && pinnedParticipants.size > 0) {
      participantIds.filter(isPinnedId).forEach((id) => {
        if (
          firstPageParticipants.length < MOBILE_PAGE_SIZE &&
          !includesById(firstPageParticipants, id)
        ) {
          firstPageParticipants.push(id);
        }
      });
    }

    if (stableVisibleParticipants && stableVisibleParticipants.length > 0) {
      stableVisibleParticipants.forEach((id) => {
        if (
          includesById(participantIds, id) &&
          !includesById(firstPageParticipants, id) &&
          firstPageParticipants.length < MOBILE_PAGE_SIZE
        ) {
          firstPageParticipants.push(id);
        }
      });
    }

    sortedParticipantIds.forEach((id) => {
      if (
        includesById(participantIds, id) &&
        !includesById(firstPageParticipants, id) &&
        firstPageParticipants.length < MOBILE_PAGE_SIZE
      ) {
        firstPageParticipants.push(id);
      }
    });

    if (activeSpeakerId && includesById(firstPageParticipants, activeSpeakerId)) {
      firstPageParticipants = [
        activeSpeakerId,
        ...filterOutById(firstPageParticipants, activeSpeakerId),
      ];
    }

    // Pinned/spotlighted always beats active speaker — promote them to position 0
    // (if multiple are pinned, the first one in props order wins).
    const pinnedInPage = firstPageParticipants.filter(isPinnedId);
    if (pinnedInPage.length > 0) {
      const nonPinned = firstPageParticipants.filter(id => !isPinnedId(id));
      firstPageParticipants = [...pinnedInPage, ...nonPinned];
    }

    const remainingParticipants = sortedParticipantIds.filter(
      (id) => includesById(participantIds, id) && !includesById(firstPageParticipants, id)
    );

    const remainingPages =
      remainingParticipants.length > 0
        ? Math.ceil(remainingParticipants.length / MOBILE_PAGE_SIZE)
        : 0;

    const totalPages = 1 + remainingPages;

    let visibleParticipants;
    if (gridCurrentPage === 0) {
      visibleParticipants = firstPageParticipants;
    } else {
      const remainingPageIndex = gridCurrentPage - 1;
      const startIndex = remainingPageIndex * MOBILE_PAGE_SIZE;
      const endIndex = startIndex + MOBILE_PAGE_SIZE;
      visibleParticipants = remainingParticipants.slice(startIndex, endIndex);
    }

    const visibleParticipantsSet = new Set(visibleParticipants);

    return (
      <div className="flex flex-col flex-1 min-w-0 w-full h-full overflow-hidden rounded-xl">
        <div className="relative flex flex-col flex-1 p-2 box-border items-center justify-center w-full max-w-full min-w-0 h-full overflow-hidden">
          {/* Render activity collectors for all participants */}
          {participantIds.map((participantId) => (
            <ParticipantActivityCollector
              key={`activity_${participantId}`}
              participantId={participantId}
              onActivityChange={handleActivityChange}
            />
          ))}

          {/* Render stream controllers for all participants */}
          {participantIds.map((participantId) => (
            <ParticipantStreamController
              key={`stream_controller_${participantId}`}
              participantId={participantId}
              isVisible={visibleParticipantsSet.has(participantId)}
              isPresenting={isPresenting}
              participantIds={participantIds}
            />
          ))}

          {/* Current page participants - Mobile responsive layout with aspect ratio */}
          {visibleParticipants.length > 0 && (
            <div className="w-full h-full flex-1 min-w-0 min-h-0 overflow-hidden flex items-center justify-center">
              {visibleParticipants.length === 1 ? (
                <div
                  className="flex items-center justify-center overflow-hidden rounded-lg w-full max-w-full"
                  style={{ aspectRatio: '9/16', maxHeight: '100%' }}
                >
                  <MemoizedParticipant participantId={visibleParticipants[0]} isSingleParticipant={true} />
                </div>
              ) : visibleParticipants.length === 2 ? (
                <div className="flex flex-col gap-2 w-full max-w-full min-w-0 items-center justify-center">
                  {visibleParticipants.map((participantId) => (
                    <div
                      key={`mobile_${participantId}`}
                      className="flex items-center justify-center overflow-hidden rounded-lg min-w-0 w-full"
                      style={{ aspectRatio: '9/16' }}
                    >
                      <MemoizedParticipant participantId={participantId} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col gap-2 w-full max-w-full min-w-0 items-center justify-center">
                  {visibleParticipants.map((participantId) => (
                    <div
                      key={`mobile_${participantId}`}
                      className="flex items-center justify-center overflow-hidden rounded-lg min-w-0 w-full"
                      style={{ aspectRatio: '9/16' }}
                    >
                      <MemoizedParticipant participantId={participantId} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {[...firstPageParticipants, ...remainingParticipants].map(
            (participantId) => {
              if (includesById(visibleParticipants, participantId)) {
                return null;
              }
              return (
                <div
                  key={`hidden_${participantId}`}
                  style={{ display: "none" }}
                >
                  <MemoizedParticipant participantId={participantId} />
                </div>
              );
            }
          )}

          {totalPages > 1 && (
            <div className="mt-3 flex w-full items-center justify-center">
              <div className="inline-flex items-center gap-3 bg-[#1C1F2E]/95 backdrop-blur-md rounded-lg px-4 py-2 shadow-lg border border-gray-700/60">
                <button
                  onClick={() => {
                    if (gridCurrentPage > 0) {
                      setGridCurrentPage(gridCurrentPage - 1);
                    }
                  }}
                  disabled={gridCurrentPage === 0}
                  className={`p-1.5 rounded transition-all duration-150 ${
                    gridCurrentPage === 0
                      ? "opacity-30 cursor-not-allowed"
                      : "hover:bg-white/10 active:scale-95"
                  }`}
                  title="Previous page"
                >
                  <img src="/icons/arrowLeft.svg" alt="Previous page" />
                </button>

                <div className="flex items-center gap-1 text-white text-sm font-medium px-2">
                  <span>{gridCurrentPage + 1}</span>
                  <span className="text-gray-400">/</span>
                  <span>{totalPages}</span>
                </div>

                <button
                  onClick={() => {
                    if (gridCurrentPage < totalPages - 1) {
                      setGridCurrentPage(gridCurrentPage + 1);
                    }
                  }}
                  disabled={gridCurrentPage >= totalPages - 1}
                  className={`p-1.5 rounded transition-all duration-150 ${
                    gridCurrentPage >= totalPages - 1
                      ? "opacity-30 cursor-not-allowed"
                      : "hover:bg-white/10 active:scale-95"
                  }`}
                  title="Next page"
                >
                  <img src="/icons/arrowRight.svg" alt="Next page" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Desktop and Presenting Layout (with active speaker sorting)
  return (
    <>
      {/* Active speaker animation styles for filmstrip */}
      <style>{`
        @keyframes filmstripSpeakerPulse {
          0%, 100% {
            box-shadow: 0 0 15px rgba(34, 197, 94, 0.5), 0 0 30px rgba(34, 197, 94, 0.25);
            border-color: rgba(34, 197, 94, 0.8);
          }
          50% {
            box-shadow: 0 0 25px rgba(34, 197, 94, 0.9), 0 0 50px rgba(34, 197, 94, 0.5), 0 0 75px rgba(34, 197, 94, 0.3);
            border-color: rgba(34, 197, 94, 1);
          }
        }
        .filmstrip-speaker-glow {
          animation: filmstripSpeakerPulse 1.5s ease-in-out infinite;
        }
      `}</style>
      <div
        ref={gridContainerRef}
        className={`relative flex flex-col md:flex-row flex-grow min-h-0 items-center justify-center rounded-xl p-2 md:p-3 ${
          isPresenting
            ? "m-0 overflow-visible"
            : "overflow-hidden"
        }`}
        style={{
          ...(isPresenting
            ? { backgroundImage: "none" }
            : branding?.activeBackground
              ? {
                  backgroundImage: `url(${branding.activeBackground})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }
              : branding?.backgroundColor
                ? {
                    // Branding → Color → Background is the host's explicit live
                    // choice during the meeting.  Apply it before falling back
                    // to persisted meetingSettings.*, so changes take effect
                    // immediately without needing a page reload.
                    backgroundColor: branding.backgroundColor,
                  }
                : meetingSettings?.meetingBackgroundImage
                  ? {
                      backgroundImage: `url(${meetingSettings.meetingBackgroundImage})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                    }
                  : meetingSettings?.meetingBackgroundGradient
                    ? {
                        backgroundImage: `linear-gradient(${meetingSettings.meetingBackgroundGradient.gradientDirection}, ${meetingSettings.meetingBackgroundGradient.gradientColor1}, ${meetingSettings.meetingBackgroundGradient.gradientColor2})`,
                      }
                    : meetingSettings?.meetingBackgroundSolid
                      ? {
                          backgroundColor: meetingSettings.meetingBackgroundSolid,
                        }
                      : {
                          backgroundImage: `url(/meetingBg.png)`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          backgroundRepeat: "no-repeat",
                        }),
        }}
      >
        {/* Render activity collectors for all participants (desktop) */}
        {participantIds.map((participantId) => (
          <ParticipantActivityCollector
            key={`activity_desktop_${participantId}`}
            participantId={participantId}
            onActivityChange={handleActivityChange}
          />
        ))}

        {/* Render stream controllers for all participants */}
        {sortedParticipantIds.map((participantId, idx) => (
          <ParticipantStreamController
            key={`stream_controller_${participantId}`}
            participantId={participantId}
            isVisible={isParticipantVisible(idx)}
            isPresenting={isPresenting}
            participantIds={participantIds}
          />
        ))}

        <div
          className={`flex ${isFlexCol ? "flex-col" : "flex-row"}  ${
            isPresenting
              ? isFlexCol
                ? `flex-col justify-start items-center gap-3 transition-all duration-300 w-full`
                : `flex-row justify-center items-center gap-3 transition-all duration-300`
              : "flex-col gap-2 items-stretch justify-center"
          } ${!isPresenting ? "w-full h-full min-h-0" : ""} relative`}
        >
          {/* Left Arrow Icon */}
          {isPresenting && totalParticipants > participantsPerPage && (
            <>
              {isFlexCol && (
                <button
                  onClick={() => {
                    if (currentPage === 0) {
                      return;
                    }
                    setCurrentPage(currentPage - 1);
                  }}
                  disabled={currentPage === 0}
                  className="flex items-center justify-center w-full h-8 rounded-md transition-colors"
                  style={{
                    zIndex: 1,
                    color: "#ffffff",
                    backgroundColor: currentPage === 0 ? "#4a4a4a" : "#1a1c23",
                    cursor: currentPage === 0 ? "not-allowed" : "pointer",
                    opacity: currentPage === 0 ? 0.5 : 1,
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="w-5 h-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4.5 15.75l7.5-7.5 7.5 7.5"
                    />
                  </svg>
                </button>
              )}
              {!isFlexCol && (
                <svg
                  onClick={() => {
                    if (currentPage == 0) {
                      return;
                    }
                    setCurrentPage(currentPage - 1);
                  }}
                  disabled={currentPage === 0}
                  style={{
                    zIndex: 1,
                    color: "#ffffff",
                    backgroundColor: currentPage == 0 ? "gray" : "#1a1c23",
                    strokeWidth: 4,
                    cursor: "pointer",
                  }}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-full"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 19.5 8.25 12l7.5-7.5"
                  />
                </svg>
              )}
            </>
          )}

          {isPresenting && (
            <>
              {/* Render all participants for audio, but hide non-current page ones */}
              {sortedParticipantIds.map((participantId, idx) => {
                const isOnCurrentPage =
                  idx >= currentPage && idx < currentPage + participantsPerPage;
                if (!isOnCurrentPage) {
                  return (
                    <div
                      key={`participant_hidden_${participantId}`}
                      style={{ display: "none" }}
                    >
                      <MemoizedParticipant participantId={participantId} />
                    </div>
                  );
                }
                return null;
              })}

              {/* Display visible participants with layout */}
              {sortedParticipantIds
                .slice(currentPage, currentPage + participantsPerPage)
                .map((participantId) => {
                  // Calculate sizes based on minimized state
                  const getParticipantSize = () => {
                    if (isParticipantRowMinimized) {
                      // Smaller sizes when minimized
                      return totalParticipants === 1
                        ? "h-12 w-[80px] sm:h-14 sm:w-[100px] md:h-14 md:w-[120px]"
                        : totalParticipants === 2
                        ? "h-12 w-[70px] sm:h-14 sm:w-[90px] md:h-14 md:w-[110px]"
                        : totalParticipants === 3
                        ? "h-11 w-[60px] sm:h-12 sm:w-[80px] md:h-13 md:w-[100px]"
                        : totalParticipants === 4
                        ? "h-10 w-[55px] sm:h-11 sm:w-[70px] md:h-12 md:w-[90px]"
                        : "h-10 w-[50px] sm:h-11 sm:w-[65px] md:h-12 md:w-[85px]";
                    } else {
                      // Normal sizes when expanded
                      return totalParticipants === 1
                        ? "h-[120px] w-[200px] sm:h-[140px] sm:w-[240px] md:h-[180px] md:w-[320px] xl:h-[200px] xl:w-[360px] 2xl:h-[220px] 2xl:w-[400px]"
                        : totalParticipants === 2
                        ? "h-[100px] w-[180px] sm:h-[120px] sm:w-[200px] md:h-[160px] md:w-[280px] xl:h-[180px] xl:w-[320px] 2xl:h-[200px] 2xl:w-[360px]"
                        : totalParticipants === 3
                        ? "h-[90px] w-[160px] sm:h-[110px] sm:w-[180px] md:h-[140px] md:w-[240px] xl:h-[160px] xl:w-[280px] 2xl:h-[180px] 2xl:w-[320px]"
                        : totalParticipants === 4
                        ? "h-[80px] w-[140px] sm:h-[100px] sm:w-[160px] md:h-[120px] md:w-[200px] xl:h-[140px] xl:w-[240px] 2xl:h-[160px] 2xl:w-[280px]"
                        : "h-[70px] w-[120px] sm:h-[90px] sm:w-[140px] md:h-[100px] md:w-[180px] xl:h-[120px] xl:w-[200px] 2xl:h-[140px] 2xl:w-[240px]";
                    }
                  };

                  // Get thumbnail size based on layout type
                  const getThumbnailSize = () => {
                    if (isFlexCol) {
                      // Speaker-side: vertical filmstrip - full width of container, fixed height
                      return "w-full h-[120px] md:h-[140px]";
                    } else {
                      // Speaker-top: horizontal filmstrip - fixed width, auto height
                      return "w-[200px] md:w-[240px] xl:w-[280px] h-[120px] md:h-[140px] xl:h-[160px]";
                    }
                  };

                  // Check if this participant is actively speaking or spotlighted
                  const isThisSpeakerActive = activeSpeakerId && String(activeSpeakerId) === String(participantId);
                  const isThisSpotlighted = pinnedParticipants && (
                    pinnedParticipants.has(participantId) || pinnedParticipants.has(String(participantId))
                  );

                  return (
                    <div
                      key={`participant_${participantId}`}
                      className={`flex ${
                        isPresenting
                          ? getThumbnailSize()
                          : "w-full"
                      } items-center justify-center flex-shrink-0 ${
                        totalParticipants <= 2 && !isPresenting
                          ? "md:max-w-[400px] 2xl:max-w-[400px] "
                          : !isPresenting
                          ? "md:max-w-lg 2xl:max-w-2xl"
                          : ""
                      } overflow-hidden rounded-xl shadow-lg transition-all duration-200 ${
                        isPresenting
                          ? `p-0 ${!isThisSpeakerActive && !isThisSpotlighted ? "hover:shadow-xl" : ""}`
                          : "p-1"
                      }`}
                      style={{
                        ...(!isPresenting ? { aspectRatio: "16/9" } : {}),
                      }}
                    >
                      <MemoizedParticipant participantId={participantId} />
                    </div>
                  );
                })}
            </>
          )}

          {/* Right Arrow Icon */}
          {isPresenting && totalParticipants > participantsPerPage && (
            <>
              {!isFlexCol && (
                <svg
                  onClick={() => {
                    if (
                      currentPage + participantsPerPage >=
                      totalParticipants
                    ) {
                      return;
                    }
                    setCurrentPage(currentPage + 1);
                  }}
                  disabled={
                    currentPage + participantsPerPage >= totalParticipants
                  }
                  style={{
                    zIndex: 1,
                    color: "#ffffff",
                    backgroundColor:
                      currentPage + participantsPerPage >= totalParticipants
                        ? "gray"
                        : "#1a1c23",
                    strokeWidth: 4,
                    cursor: "pointer",
                  }}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-full"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8.25 4.5 15.75 12l-7.5 7.5"
                  />
                </svg>
              )}
              {isFlexCol && (
                <button
                  onClick={() => {
                    if (
                      currentPage + participantsPerPage >=
                      totalParticipants
                    ) {
                      return;
                    }
                    setCurrentPage(currentPage + 1);
                  }}
                  disabled={
                    currentPage + participantsPerPage >= totalParticipants
                  }
                  className="flex items-center justify-center w-full h-8 rounded-md transition-colors"
                  style={{
                    zIndex: 1,
                    color: "#ffffff",
                    backgroundColor:
                      currentPage + participantsPerPage >= totalParticipants
                        ? "#4a4a4a"
                        : "#1a1c23",
                    cursor:
                      currentPage + participantsPerPage >= totalParticipants
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      currentPage + participantsPerPage >= totalParticipants
                        ? 0.5
                        : 1,
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="w-5 h-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                    />
                  </svg>
                </button>
              )}
            </>
          )}

          {!isPresenting &&
            (() => {
              // Calculate paginated participants for grid view (using sorted list)
              const startIndex = gridCurrentPage * gridParticipantsPerPage;
              const endIndex = startIndex + gridParticipantsPerPage;
              const paginatedParticipants = sortedParticipantIds.slice(
                startIndex,
                endIndex
              );

              // Calculate perRow based on current page participants, not total participants
              const currentPageParticipants = paginatedParticipants.length;

              // Cap columns based on actual available container width
              // so the grid adapts when sidebars shrink the available space
              const MIN_TILE_WIDTH = 200; // minimum px per tile to remain legible
              const maxColsByWidth = containerWidth > 0
                ? Math.max(1, Math.floor((containerWidth - 24) / MIN_TILE_WIDTH)) // subtract padding
                : 5; // fallback before first measurement

              const perRowByCount =
                isMobile || isPresenting
                  ? currentPageParticipants < 4
                    ? 1
                    : currentPageParticipants < 9
                    ? 2
                    : 3
                  : currentPageParticipants === 2
                  ? 2 // For 2 participants, use 2 columns (side by side)
                  : currentPageParticipants < 5
                  ? 2
                  : currentPageParticipants < 7
                  ? 3
                  : currentPageParticipants < 9
                  ? 4
                  : currentPageParticipants < 10
                  ? 3
                  : currentPageParticipants < 13
                  ? 4
                  : 5;

              // Use the smaller of participant-based and width-based column counts
              const perRow = Math.min(perRowByCount, maxColsByWidth);

              return (
                <>
                  {/* Render all participants for audio, but hide non-current page ones */}
                  {sortedParticipantIds.map((participantId, idx) => {
                    const isOnCurrentPage = idx >= startIndex && idx < endIndex;
                    if (!isOnCurrentPage) {
                      return (
                        <div
                          key={`participant_hidden_${participantId}`}
                          style={{ display: "none" }}
                        >
                          <MemoizedParticipant participantId={participantId} />
                        </div>
                      );
                    }
                    return null;
                  })}

                  {/* Display visible participants with layout */}
                  {(() => {
                    if (
                      (totalParticipants === 1 ||
                        currentPageParticipants === 1 ||
                        isWebinarRecording) &&
                      !isMobile
                    ) {
                      return (
                        <div className="flex flex-1 items-center justify-center h-full w-full">
                          {paginatedParticipants.map((participantId, index) => {
                            return (
                              <div
                                key={`participant_${participantId}`}
                                className="flex items-center justify-center overflow-hidden rounded-xl w-full h-full"
                              >
                                <MemoizedParticipant
                                  participantId={participantId}
                                  isSingleParticipant={true}
                                />
                              </div>
                            );
                          })}
                        </div>
                      );
                    }

                    // Special handling for 2 participants - display them side by side (half width each)
                    // Apply this only on non-mobile screens; on mobile, stack vertically
                    // Webinar recorder mode always uses full-screen layout to avoid empty half-screen slots
                    if (
                      (totalParticipants === 2 ||
                        currentPageParticipants === 2) &&
                      !isMobile &&
                      !isWebinarRecording
                    ) {
                      return (
                        <div className="flex flex-1 items-center justify-center h-full w-full gap-3">
                          {paginatedParticipants.map((participantId, index) => {
                            return (
                              <div
                                key={`participant_${participantId}`}
                                className="flex w-1/2 items-center justify-center overflow-hidden rounded-xl"
                                style={{ aspectRatio: "16/9" }}
                              >
                                <MemoizedParticipant
                                  participantId={participantId}
                                />
                              </div>
                            );
                          })}
                        </div>
                      );
                    }

                    // Calculate pixel-perfect tile sizes to maintain 16:9 aspect ratio for all participant counts
                    const numRows = Math.ceil(paginatedParticipants.length / perRow);

                    // All participant counts (3+): calculated pixel sizing for proper 16:9 aspect ratio
                    const GAP = 8; // gap-2 = 8px
                    const PADDING = 24; // p-3 = 12px * 2
                    const availW = (containerWidth || 800) - PADDING;
                    const availH = (containerHeight || 600) - PADDING;

                    // Tile size if width-constrained
                    const tileWFromWidth = (availW - (perRow - 1) * GAP) / perRow;
                    const tileHFromWidth = tileWFromWidth * 9 / 16;
                    const totalHFromWidth = tileHFromWidth * numRows + (numRows - 1) * GAP;

                    // Choose: if width-based fits in height, use it; otherwise size from height
                    let tileW, tileH;
                    if (totalHFromWidth <= availH) {
                      tileW = tileWFromWidth;
                      tileH = tileHFromWidth;
                    } else {
                      tileH = (availH - (numRows - 1) * GAP) / numRows;
                      tileW = tileH * 16 / 9;
                    }

                    return (
                      <div
                        className="flex flex-col items-center justify-center w-full h-full min-h-0"
                        style={{ gap: GAP }}
                      >
                        {Array.from({ length: numRows }, (_, i) => (
                          <div
                            key={`participant-${i}`}
                            className="flex items-center justify-center"
                            style={{ gap: GAP }}
                          >
                            {paginatedParticipants
                              .slice(i * perRow, (i + 1) * perRow)
                              .map((participantId) => {
                                return (
                                  <div
                                    key={`participant_${participantId}`}
                                    className="flex items-center justify-center overflow-hidden rounded-xl"
                                    style={{
                                      width: tileW,
                                      height: tileH,
                                      maxWidth: '100%',
                                    }}
                                  >
                                    <MemoizedParticipant
                                      participantId={participantId}
                                    />
                                  </div>
                                );
                              })}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </>
              );
            })()}
        </div>

        {/* Grid Pagination Controls moved to StudioBottomControls via context */}
      </div>
    </>
  );
}

export const MemoizedParticipantGrid = React.memo(
  ParticipantGrid,
  (prevProps, nextProps) => {
    return (
      JSON.stringify(prevProps.participantIds) ===
        JSON.stringify(nextProps.participantIds) &&
      prevProps.isPresenting === nextProps.isPresenting
    );
  }
);
